<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ $t('auditList') }}
        </v-card-title>
        <div class="px-3 py-2">
          <div class="d-flex flex-row align-start flex-wrap">
            <div class="mr-2">
              <div class="datepicker-label mt-n2">
                {{ $t('from') }}
              </div>
              <date-time-picker
                v-model="from"
                date-id="60f3f16c"
                time-id="1e232517"
              />
            </div>
            <div class="mr-2">
              <div class="datepicker-label mt-n2">
                {{ $t('to') }}
              </div>
              <date-time-picker
                v-model="to"
                date-id="aba0d56e"
                time-id="fd4d6af5"
              />
            </div>
            <div class="mr-3 event">
              <v-select
                id="fa1fba1f"
                v-model="event"
                :label="$t('event')"
                name="event"
                :items="mergedEventTypeList"
                menu-props="auto"
                hide-details
                single-line
              ></v-select>
            </div>
            <div class="mr-2">
              <v-text-field
                id="7800ec60"
                v-model="object"
                :label="$t('object')"
                single-line
                hide-details
              ></v-text-field>
            </div>
            <div class="mr-2">
              <v-text-field
                id="dd963f8e"
                v-model="subject"
                :label="$t('subject')"
                single-line
                hide-details
              ></v-text-field>
            </div>
            <div class="mr-3">
              <v-btn
                id="b4c2cf45"
                color="primary"
                @click="searchAudit"
              >
                <v-icon left>
                  mdi-magnify
                </v-icon>
                {{ $t('search') }}
              </v-btn>
            </div>
            <div class="mr-3">
              <v-btn
                id="61bf8c1d"
                @click="resetSearchAreas"
              >
                {{ $t('reset') }}
              </v-btn>
            </div>
            <div>
              <v-btn
                id="2b9ccb81"
                color="green"
                @click="exportAuditLog"
              >
                <v-icon left>
                  mdi-file-export-outline
                </v-icon>
                {{ $t('export') }}
              </v-btn>
            </div>
          </div>
          <v-data-table
            id="43ba000d"
            must-sort
            :headers="headers"
            :items="auditList"
            :options.sync="pagination"
            :server-items-length="totalItems"
            :loading="loading"
            :loading-text="$t('dataTable.loadingPleaseWait')"
            :no-data-text="$t('dataTable.noDataAvailable')"
            :no-results-text="$t('dataTable.noMatchingRecordsFound')"
            :footer-props="{
              'items-per-page-text': this.$t('dataFooter.itemsPerPage'),
              'page-text': this.$t('dataFooter.pageText'),
              'items-per-page-options': [10,25,50]
            }"
          >
            <template v-slot:item.loggedAt="{ item }">
              <span>{{ convertAuditDate(item.loggedAt) }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import audit from '@/api/audit'
import baseEventTypePartOne from './AuditBaseEventType_Part_One'
import baseEventTypePartTwo from './AuditBaseEventType_Part_Two'
import tspEventType from './AuditTspEventType'
import dssEventType from './AuditDssEventType'
import ocspEventType from './AuditOcspEventType'
import Feature from '../../Feature'
import DateTimePicker from '../../components/DateTimePicker.vue'
import moment from 'moment'
import FileSaver from 'file-saver'
import { mapGetters } from 'vuex'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'AuditList',

  components: {
    'date-time-picker': DateTimePicker
  },

  data() {
    return {
      from: null,
      to: null,
      event: null,
      object: null,
      subject: null,
      pagination: {
        sortBy: ['loggedAt'],
        sortDesc: ['desc']
      },
      auditList: [],
      totalItems: 0,
      loading: false,
      headers: [
        {
          text: this.$t('event'),
          align: 'left',
          value: 'event'
        },
        {
          text: this.$t('loggedAt'),
          value: 'loggedAt'
        },
        {
          text: this.$t('object'),
          value: 'object'
        },
        {
          text: this.$t('subject'),
          value: 'subject'
        },
        {
          text: this.$t('message'),
          value: 'message'
        }
      ]
    }
  },

  computed: {
    ...mapGetters(['hasFeatures']),
    isTSP() {
      return this.hasFeatures(Feature.TSP)
    },
    isDSS() {
      return this.hasFeatures(Feature.DSS)
    },
    isOCSP() {
      return this.hasFeatures(Feature.OCSP)
    },
    mergedEventTypeList() {
      return [
        ...baseEventTypePartOne,
        ...(this.isTSP ? tspEventType : []),
        ...(this.isDSS ? dssEventType : []),
        ...(this.isOCSP ? ocspEventType : []),
        ...baseEventTypePartTwo
      ]
    }
  },

  watch: {
    pagination() {
      this.searchAudit()
    }
  },

  methods: {
    async searchAudit() {
      this.loading = true
      try {
        let pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }

        let q = {
          'from': this.from == null || this.from === undefined ? null : this.from,
          'to': this.to == null || this.to === undefined ? null : this.to,
          'event': this.event == null ? null : this.event,
          'object': this.object == null ? null : this.object,
          'subject': this.subject == null ? null : this.subject
        }

        let page = await audit.listLogs(q, pageRequest)
        this.auditList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      } finally {
        this.loading = false
      }
    },

    resetSearchAreas() {
      this.from = null
      this.to = null
      this.object = null
      this.subject = null
      this.event = null
    },

    async exportAuditLog() {
      let q = {
        'event': this.event == null ? null : this.event,
        'subject': this.subject == null ? null : this.subject,
        'object': this.object == null ? null : this.object,
        'from': this.from == null || this.from === undefined ? null : this.from,
        'to': this.to == null || this.to === undefined ? null : this.to
      }

      try {
        await FileSaver.saveAs(await audit.exportAuditLogFile(q), moment(new Date()).format('YYYYMMDD-HHmmss') + '-exportedAuditLogs.csv')
        showToastSuccess(this.$t('toastMessage.succesfullyExported'))
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    }
  }
}
</script>

<style>
.datepicker-label {
  font-size: 10pt;
}

.datetimepicker .v-text-field {
  padding-top: 0px;
  padding-bottom: 0px;
}

.event {
  width: 210pt;
}
</style>
